import { createApi } from '@reduxjs/toolkit/query/react';
//
import { customBaseQuery } from '../../auth/context/jwt/utils';
import {
  IContract,
  IContractCreate,
  IContractMilestone,
  IContractPage,
  IContractSummary,
} from '../../types/contract';

const path = 'contracts';

type UpdateProps = {
  data: IContractCreate;
  id: number;
};

export const contractService = createApi({
  reducerPath: 'contractService',
  tagTypes: ['contracts'],
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getContracts: builder.query<IContractPage, Record<string, unknown>>({
      query: (params) => ({
        url: `/${path}/list`,
        params,
      }),
      providesTags: ['contracts'],
    }),

    getContractSummary: builder.query<IContractSummary, number>({
      query: (clientId) => ({ url: `/${path}/summary`, params: { clientId } }),
      providesTags: ['contracts'],
    }),

    getContract: builder.query<IContract, number>({
      query: (id: number) => ({
        url: `/${path}/findById/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'contracts', id }],
    }),

    saveContract: builder.mutation<IContract, IContractCreate>({
      query: (body) => ({
        url: `/${path}/insert`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['contracts'],
    }),

    updateContract: builder.mutation<IContract, UpdateProps>({
      query: ({ id, data }) => ({
        url: `/${path}/update/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => ['contracts', { type: 'contracts', id }],
    }),

    deleteContract: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `/${path}/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['contracts'],
    }),

    submitContract: builder.mutation<IContract, number>({
      query: (id) => ({ url: `/${path}/${id}/submit`, method: 'POST' }),
      invalidatesTags: ['contracts'],
    }),

    approveContract: builder.mutation<IContract, number>({
      query: (id) => ({ url: `/${path}/${id}/approve`, method: 'POST' }),
      invalidatesTags: ['contracts'],
    }),

    activateContract: builder.mutation<IContract, number>({
      query: (id) => ({ url: `/${path}/${id}/activate`, method: 'POST' }),
      invalidatesTags: ['contracts'],
    }),

    suspendContract: builder.mutation<IContract, number>({
      query: (id) => ({ url: `/${path}/${id}/suspend`, method: 'POST' }),
      invalidatesTags: ['contracts'],
    }),

    completeContract: builder.mutation<IContract, number>({
      query: (id) => ({ url: `/${path}/${id}/complete`, method: 'POST' }),
      invalidatesTags: ['contracts'],
    }),

    cancelContract: builder.mutation<IContract, number>({
      query: (id) => ({ url: `/${path}/${id}/cancel`, method: 'POST' }),
      invalidatesTags: ['contracts'],
    }),

    addMilestone: builder.mutation<IContract, { id: number; data: IContractMilestone }>({
      query: ({ id, data }) => ({
        url: `/${path}/${id}/milestones`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['contracts'],
    }),

    updateMilestone: builder.mutation<
      IContract,
      { id: number; milestoneId: number; data: IContractMilestone }
    >({
      query: ({ id, milestoneId, data }) => ({
        url: `/${path}/${id}/milestones/${milestoneId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['contracts'],
    }),

    deleteMilestone: builder.mutation<IContract, { id: number; milestoneId: number }>({
      query: ({ id, milestoneId }) => ({
        url: `/${path}/${id}/milestones/${milestoneId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['contracts'],
    }),
  }),
});

export const {
  useGetContractsQuery,
  useGetContractQuery,
  useGetContractSummaryQuery,
  useSaveContractMutation,
  useUpdateContractMutation,
  useDeleteContractMutation,
  useSubmitContractMutation,
  useApproveContractMutation,
  useActivateContractMutation,
  useSuspendContractMutation,
  useCompleteContractMutation,
  useCancelContractMutation,
  useAddMilestoneMutation,
  useUpdateMilestoneMutation,
  useDeleteMilestoneMutation,
} = contractService;
